import { downloadAsJpeg } from 'Cloud/Application/DownloadAsJpeg';
import config from 'Cloud/config';
import classNames from 'clsx';
import { ReactComponent as CloneIcon } from 'img/icons/add2Cloud.svg';
import { ReactComponent as DownloadAppIcon } from 'img/icons/downloadApp.svg';
import { ReactComponent as FullScreenIcon } from 'img/icons/fullScreen.svg';
import { ReactComponent as CloudIcon } from 'mrg-icons/src/mailru/categories/cloud.svg';
import React, { PureComponent } from 'react';
import { goToRegisterPage } from 'reactApp/modules/ph/ph.helpers';
import { Button } from 'reactApp/ui/Button/Button';
import Name from 'reactApp/ui/Name/Name';
import { StickyComponent } from 'reactApp/ui/StickyComponent/StickyComponent';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ViewerShareButton } from 'reactApp/ui/ViewerShareButton/ViewerShareButton';
import { DownloadIcon } from 'reactApp/ui/VKUIIcons';
import { formatDate, formatExpires } from 'reactApp/utils/formatDate';
import { createGaSender, sendXray } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';

import styles from './ViewerToolbar.css';

const sendGaAb = createGaSender('pubfile');

const IS_WEBVIEW = config.get('IS_WEBVIEW');

interface IProps {
    isAuthorized: boolean;
    file: {
        name: string;
        size: string;
        extension: string;
        date: number;
        expires: number;
    };
    onSave(): void;
    onClone(): void;
    onLogin(): void;
    onShare(): void;
    socialList: any[];
    onSocial(id: string): void;
    isIOS: boolean;
    onDownload(useDownloadUrl: boolean): void;
    canClone: boolean;
    onFullScreen(): void;
    isPhone: boolean;
    isMobile: boolean;
    showFullScreen: boolean;
    sendGaRadar(action: string): void;
}

export class ViewerToolbar extends PureComponent<IProps> {
    static defaultProps = {
        showFullScreen: true,
        sendGaRadar: noop,
    };

    componentDidMount() {
        if (this.props.isPhone && !IS_WEBVIEW) {
            sendXray(['public-file-view', 'ab-appdd', 'blue']);
            sendGaAb('blu', 'show');
        }

        if (downloadAsJpeg.isAvailable({ ext: this.props.file.extension })) {
            downloadAsJpeg.sendGa({ event: 'show', label: 'public' });
        }
    }

    sendGA = (action) => {
        sendXray(['publ-viewic-file', action, this.props.isAuthorized ? 'user' : 'anon']);

        this.props.sendGaRadar(action);
    };

    handleOnSocial = (id) => {
        this.sendGA('social-click');

        this.props.onSocial(id);
    };

    handleOnClone = () => {
        const { isAuthorized, isPhone, onLogin, onClone } = this.props;
        const eventAction = isAuthorized ? 'clone-click' : 'auth-click';

        this.sendGA(eventAction);

        // 'public-file' - общая категория событий для белого и черного одиночного паблика
        sendXray(['public-file', eventAction]);

        if (isAuthorized) {
            onClone();
        } else if (isPhone) {
            onLogin();
        } else {
            goToRegisterPage();
        }
    };

    handleOnMobileApp = () => {
        const deepLink = `?mt_deeplink=${window.location.href}`;
        const link = 'https://trk.mail.ru/c/kw1fh0';

        sendXray(['public-file-view', 'dd-ab-click', 'blu']);
        sendXray(['public-file-view_dl-ab-click_blu'], null, { sendImmediately: true });
        sendGaAb('blu', 'click', null, true);
        setTimeout(() => {
            window.location.href = link + deepLink;
        }, 100);
    };

    handleOnDownload = ({ useDownloadUrl = false }: { useDownloadUrl?: boolean } | any = {}) => {
        const eventAction = 'download-click';
        this.sendGA(eventAction);

        // 'public-file' - общая категория событий для белого и черного одиночного паблика
        sendXray(['public-file', eventAction]);

        if (downloadAsJpeg.isAvailable({ ext: this.props.file.extension })) {
            downloadAsJpeg.sendGa({ ext: this.props.file.extension, label: 'public', asJpeg: useDownloadUrl });
        }

        this.props.onDownload(useDownloadUrl);
    };

    renderDownloadAppBtn = () => {
        const button = (
            <Button onClick={this.handleOnMobileApp} color="blue" size="big">
                <DownloadAppIcon className={styles.downloadAppIcon2} />
                Скачать приложение
            </Button>
        );

        return <StickyComponent fixedContentClassName={styles.downloadApp_fixed} offset={22} renderContent={() => button} />;
    };

    renderMobile = () => {
        const { file, canClone } = this.props;
        const date = file.date ? formatDate(file.date) : '';
        const expires = file.expires ? formatExpires(file.expires) : '';

        return (
            <div
                className={classNames({
                    [styles.root]: true,
                    [styles.root_mobile]: true,
                })}
            >
                <div className={styles.content}>
                    <div className={styles.info}>
                        <div className={styles.name}>
                            <Name name={file.name} extension={file.extension} />
                        </div>
                        <div className={styles.meta}>
                            {file.size}
                            {date ? `, ${date}` : ''}
                        </div>
                    </div>
                    <div className={styles.download}>
                        {canClone && (
                            <Button onClick={this.handleOnClone} color="black" size="big" data-name="clone">
                                <CloneIcon width={24} height={24} viewBox="12 12 24 24" />
                            </Button>
                        )}
                        <Button onClick={this.handleOnDownload} color="black" size="big" data-name="download">
                            <DownloadIcon />
                        </Button>
                    </div>
                </div>
                {expires && <div className={styles.expires}>Доступ по ссылке открыт до {expires}</div>}
                <div className={styles.actions}>
                    {IS_WEBVIEW ? (
                        <ViewerShareButton
                            onSocial={this.handleOnSocial}
                            socialList={this.props.socialList}
                            placement={tooltipPlacements.TOP_LEFT}
                        />
                    ) : (
                        <div className={styles.downloadAppBttnWrapper}>{this.renderDownloadAppBtn()}</div>
                    )}
                </div>
            </div>
        );
    };

    render() {
        const { file, isAuthorized, canClone, onFullScreen, isPhone, isMobile, socialList, showFullScreen, sendGaRadar } = this.props;
        const showCloneBtn = canClone || !isAuthorized;

        if (isPhone) {
            return this.renderMobile();
        }

        const date = file.date ? formatDate(file.date) : '';
        const expires = file.expires ? formatExpires(file.expires) : '';
        const theme = showCloneBtn ? '' : 'octavius';
        const color = showCloneBtn ? 'black' : undefined;

        return (
            <div className={styles.root}>
                <div className={styles.left}>
                    {canClone && (
                        <div className={styles.action}>
                            <Button primary onClick={this.handleOnClone} size="big" data-name="actionWithCloud" theme="octavius">
                                <CloudIcon className={styles.buttonIcon} fill="currentColor" />
                                {isAuthorized ? 'Сохранить в Облако' : 'Завести Облако'}
                            </Button>
                        </div>
                    )}
                    <div className={styles.download}>
                        {downloadAsJpeg.isAvailable({ ext: file.extension }) ? (
                            <>
                                <Button
                                    primary={!showCloneBtn}
                                    onClick={() => this.handleOnDownload({ useDownloadUrl: true })}
                                    color={color}
                                    data-name="downloadAsJpeg"
                                    theme={theme}
                                >
                                    Скачать в jpeg
                                </Button>
                                <Button onClick={this.handleOnDownload} color="black" data-name="downloadOriginal">
                                    Скачать в {file.extension.toLowerCase()}
                                </Button>
                            </>
                        ) : (
                            <Button
                                primary={!showCloneBtn}
                                onClick={this.handleOnDownload}
                                color={color}
                                data-name="download"
                                theme={theme}
                            >
                                Скачать
                            </Button>
                        )}
                    </div>
                    <div className={styles.info}>
                        <div className={styles.name}>
                            <Name name={file.name} extension={file.extension} />
                        </div>
                        <div className={styles.meta}>
                            {file.size}
                            {date ? `, ${date}` : ''}
                        </div>
                    </div>
                </div>
                <div className={styles.right}>
                    {expires && (
                        <div className={styles.expires}>
                            Доступ по ссылке открыт
                            <br />
                            до {expires}
                        </div>
                    )}

                    {!isMobile && showFullScreen && (
                        <div className={styles.action}>
                            <Button color="black" onClick={onFullScreen}>
                                <FullScreenIcon />
                            </Button>
                        </div>
                    )}
                    <ViewerShareButton
                        onSocial={this.handleOnSocial}
                        socialList={socialList}
                        placement={tooltipPlacements.TOP_LEFT}
                        sendGaRadar={sendGaRadar}
                    />
                </div>
            </div>
        );
    }
}
